import { useMemo, useState } from "react";
import { AdminTableSkeleton } from "../../components/admin/AdminTableSkeleton";
import { Toast, useToast } from "../../components/Toast";
import { useAdminClaws } from "../../hooks/useAdminClaws";
import { type TenantPlugin, useAdminPlugins } from "../../hooks/useAdminPlugins";
import { formatDateTime, formatDateTimeCompact } from "../../utils/formatDateTime";
import styles from "../AdminInstances.module.css";
import pluginStyles from "./AdminPlugins.module.css";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";

// Install a ZTC OpenClaw plugin onto one instance.
//
// The catalog is what this build can offer, generated from plugins/ and
// shipped in the image. The per-instance rows are the entitlement, which is
// the only thing that decides what a box holds.
//
// An install takes minutes: the POST claims the row and returns, the row reads
// `installing`, and the hook polls until it lands or fails. Nothing here waits
// on the instance.

const STATE_LABEL: Record<TenantPlugin["state"], string> = {
  available: "Not installed",
  pending: "Queued",
  installing: "Installing",
  installed: "Installed",
  failed: "Failed",
};

export const AdminPlugins = (): JSX.Element => {
  const { claws, status: clawsStatus } = useAdminClaws();
  const { toast, showToast, dismissToast } = useToast();

  const [tenantId, setTenantId] = useState<string>("");
  const [pluginId, setPluginId] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);

  const selectedTenantId = tenantId === "" ? null : tenantId;
  const { catalogStatus, catalog, catalogError, tenantStatus, tenantPlugins, tenantError, isFetchingTenant, install, remove } =
    useAdminPlugins(selectedTenantId);

  // Only instances that have actually been provisioned can take an install,
  // and offering the rest just produces a rejected request.
  const installable = useMemo(
    () => claws.filter((claw) => (claw.instanceId ?? null) !== null),
    [claws],
  );

  const selectedClaw = installable.find((claw) => claw.tenantId === tenantId) ?? null;
  const canInstall = selectedTenantId !== null && pluginId !== "" && !busy;

  const handleInstall = async (): Promise<void> => {
    if (selectedTenantId === null || pluginId === "") return;
    setBusy(true);
    const result = await install(selectedTenantId, pluginId);
    setBusy(false);
    showToast(
      result.ok
        ? `Installing ${pluginId} on ${selectedClaw?.name ?? selectedTenantId}. This takes a few minutes and restarts the gateway.`
        : `Install failed: ${result.message}`,
      result.ok ? "success" : "error",
    );
  };

  const handleRemove = async (plugin: TenantPlugin): Promise<void> => {
    if (selectedTenantId === null) return;
    setBusy(true);
    const result = await remove(selectedTenantId, plugin.pluginId);
    setBusy(false);
    showToast(
      result.ok
        ? `Removed the ${plugin.pluginId} entitlement. The plugin is still installed on the box.`
        : `Remove failed: ${result.message}`,
      result.ok ? "success" : "error",
    );
  };

  const rows = tenantPlugins;

  return (
    <div className={styles.page}>
      <AdminPageIntro title="Plugins">
        OpenClaw plugins built in this repo. Installing restarts the instance&apos;s gateway and briefly drops its channels, so pick a quiet window.
      </AdminPageIntro>

      <section className={pluginStyles.installPanel} data-testid="admin-plugin-install">
        <div className={pluginStyles.field}>
          <label className={pluginStyles.label} htmlFor="plugin-instance">
            Instance
          </label>
          <select
            id="plugin-instance"
            className={pluginStyles.select}
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
            data-testid="admin-plugin-instance-select"
          >
            <option value="">Select an instance</option>
            {installable.map((claw) => (
              <option key={claw.tenantId} value={claw.tenantId}>
                {claw.name} ({claw.tenantId})
              </option>
            ))}
          </select>
        </div>

        <div className={pluginStyles.field}>
          <label className={pluginStyles.label} htmlFor="plugin-id">
            Plugin
          </label>
          <select
            id="plugin-id"
            className={pluginStyles.select}
            value={pluginId}
            onChange={(event) => setPluginId(event.target.value)}
            data-testid="admin-plugin-select"
          >
            <option value="">Select a plugin</option>
            {catalog.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name} {entry.version}
                {entry.default ? " (default, every instance)" : ""}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          className={pluginStyles.installButton}
          disabled={!canInstall}
          onClick={() => void handleInstall()}
          data-testid="admin-plugin-install-button"
        >
          {busy ? "Working..." : "Install"}
        </button>
      </section>

      {catalogStatus === "error" && (
        <div className={styles.empty}>{catalogError ?? "Failed to load the plugin catalog."}</div>
      )}
      {catalogStatus === "ready" && catalog.length === 0 && (
        <div className={styles.empty} data-testid="admin-plugin-catalog-empty">
          No plugins are built yet. Scaffold one with <code>bin/plugin new</code>.
        </div>
      )}

      <section className={styles.listSection}>
        {selectedTenantId === null && (
          <div className={styles.empty}>Select an instance to see what it holds.</div>
        )}
        {selectedTenantId !== null && tenantStatus === "loading" && (
          <AdminTableSkeleton
            headers={["Plugin", "State", "Installed", "Available", "Updated", ""]}
            tableClassName={`${styles.table ?? ""} ${pluginStyles.compactTable ?? ""}`}
          />
        )}
        {selectedTenantId !== null && tenantStatus === "error" && (
          <div className={styles.empty}>{tenantError ?? "Failed to load."}</div>
        )}
        {selectedTenantId !== null && tenantStatus === "ready" && rows.length === 0 && (
          <div className={styles.empty}>Nothing to show for this instance.</div>
        )}
        {selectedTenantId !== null && tenantStatus === "ready" && rows.length > 0 && (
          <table
            className={`${styles.table} ${pluginStyles.compactTable}`}
            data-testid="admin-plugin-table"
            data-fetching={isFetchingTenant ? "true" : "false"}
          >
            <thead>
              <tr>
                <th>Plugin</th>
                <th>State</th>
                <th>Installed</th>
                <th>Available</th>
                <th>Updated</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((plugin) => (
                <tr key={plugin.pluginId} data-testid="admin-plugin-row">
                  <td>
                    <div className={pluginStyles.name}>
                      {plugin.name}
                      {plugin.default && (
                        <span className={pluginStyles.defaultTag} data-testid="admin-plugin-default-tag">
                          Default
                        </span>
                      )}
                    </div>
                    <div className={`${pluginStyles.secondary} ${pluginStyles.clamp}`} title={plugin.description}>
                      {plugin.description}
                    </div>
                    {plugin.state === "failed" && plugin.lastError !== null && (
                      <div className={pluginStyles.error}>{plugin.lastError}</div>
                    )}
                  </td>
                  <td data-testid={`admin-plugin-state-${plugin.pluginId}`}>
                    <span className={pluginStyles[`state_${plugin.state}`] ?? ""}>
                      {STATE_LABEL[plugin.state]}
                    </span>
                  </td>
                  <td>
                    {plugin.installedVersion ?? plugin.boxVersion ?? "-"}
                    {plugin.installedVersion === null && plugin.boxVersion !== null && (
                      // Nothing asked for this one in the database: the version
                      // is what the box reported it is actually running.
                      <span className={pluginStyles.secondary}> on box</span>
                    )}
                  </td>
                  <td>
                    {plugin.catalogVersion ?? (
                      // A row whose plugin has left the catalog still matters:
                      // the instance is running it.
                      <span className={pluginStyles.secondary}>not in this build</span>
                    )}
                  </td>
                  <td className={styles.nowrap} title={formatDateTime(plugin.updatedAt)}>
                    {formatDateTimeCompact(plugin.updatedAt)}
                  </td>
                  <td>
                    {/* Remove deletes the entitlement row, so it is offered
                        exactly when there is one. A default reported only by
                        the box has none; one an admin installed by hand does. */}
                    {plugin.entitled && (
                      <button
                        type="button"
                        className={pluginStyles.linkButton}
                        disabled={busy}
                        onClick={() => void handleRemove(plugin)}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {clawsStatus === "error" && (
          <div className={styles.empty}>Failed to load instances.</div>
        )}
      </section>

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};
